import { useState, useEffect, useMemo, useId } from 'react'
import { useParams, useNavigate, useMatch, Link, NavLink } from 'react-router-dom'
import { ChevronLeft, Save, Trash2, AlertCircle, List, LayoutTemplate, FileText, Globe } from 'lucide-react'
import jsyaml from 'js-yaml'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import { useBuilderObjectType, useBuilderObjectTypes } from './hooks/useBuilderObjectTypes'
import { useBuilderObjects } from './hooks/useBuilderObjects'
import { useBuilderModelsFor } from './hooks/useBuilderPages'
import { useBuilderSites } from './hooks/useBuilderSites'
import { BuilderTopHeader } from './BuilderNavTabs'
import { BuilderTypeModelTab } from './BuilderTypeModelTab'

/**
 * L'écran d'UN type de contenu : trois onglets, adressables par l'URL.
 *
 *   /builder/types/:type          Contenus — ses contenus en tableau. Premier
 *                                 onglet : c'est ce qu'on vient faire ici.
 *   /builder/types/:type/model    Modèle — la page qui rend les contenus à
 *                                 leur adresse publique (chemin,
 *                                 correspondance, accès à l'éditeur de blocs).
 *   /builder/types/:type/schema   Schéma — le YAML brut des champs (l'éditeur
 *                                 structuré viendra dans un chantier à part).
 *
 * Un type de contenu = un schéma + UN modèle + ses contenus, rattachés à un
 * site.
 *
 * Le tableau des contenus est un rjBuild (/pages/builder/type-contents.yaml :
 * Elasticsearch, recherche, pagination, corbeille), monté ici avec les valeurs
 * du type injectées. Seul l'écran qui l'héberge est React — il l'était déjà
 * (schéma, suppression), et l'onglet « Modèle » l'exige (cf. son en-tête).
 */

const CONTENTS_RJBUILD = '/pages/builder/type-contents.yaml'

// Le rjBuild ne change pas d'un type à l'autre : téléchargé une fois par
// session, pas à chaque retour sur l'onglet (qui démonte le tableau). Un échec
// n'est pas mis en cache — le prochain montage retente.
let contentsRjBuildPromise = null
const loadContentsRjBuild = () => {
  if (!contentsRjBuildPromise) {
    contentsRjBuildPromise = fetch(CONTENTS_RJBUILD)
      .then(res => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
      .catch((err) => {
        contentsRjBuildPromise = null
        throw err
      })
  }
  return contentsRjBuildPromise
}

const TABS = [
  { id: 'contents', suffix: '', label: 'Contenus', icon: List },
  { id: 'model', suffix: '/model', label: 'Modèle', icon: LayoutTemplate },
  { id: 'schema', suffix: '/schema', label: 'Schéma', icon: FileText },
]

const Spinner = ({ label = 'Chargement…' }) => (
  <div className="flex items-center justify-center py-16" role="status">
    <span className="spinner" aria-hidden="true" />
    <span className="sr-only">{label}</span>
  </div>
)

export const BuilderTypeShell = ({ embedded = false, plugins }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50`

  const { objectType: machineName } = useParams()
  // L'onglet se lit sur la ROUTE, pas sur la fin de l'URL : un type nommé
  // `model` ou `schema` ne doit pas ouvrir le mauvais onglet.
  const onModel = useMatch('/builder/types/:objectType/model')
  const onSchema = useMatch('/builder/types/:objectType/schema')
  const tab = onModel ? 'model' : (onSchema ? 'schema' : 'contents')

  const { type, loading: typeLoading, error: typeError, reload: reloadType } = useBuilderObjectType(machineName)
  const { models, loading: modelsLoading, reload: reloadModels } = useBuilderModelsFor(machineName)

  // Spinner au PREMIER chargement seulement : une relecture (après un
  // enregistrement du schéma, un rattachement) ne doit pas démonter l'onglet —
  // le focus et le message de succès partiraient avec.
  if (typeLoading && !type) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <Spinner label="Chargement du type de contenu…" />
      </div>
    )
  }

  if (typeError || !type) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <AlertCircle size={28} className="text-amber-500 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-gray-600">
            Type de contenu « {machineName} » introuvable.
          </p>
          <Link
            to="/builder/types"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-700 hover:text-teal-900 transition-colors"
          >
            <ChevronLeft size={14} aria-hidden="true" />
            Retour aux types de contenu
          </Link>
        </div>
      </div>
    )
  }

  const base = `/builder/types/${type.type}`
  // 1 type = 1 modèle ; une liste plus longue est un héritage que l'onglet
  // « Modèle » signale. Ici, pour l'aperçu et « Modifier dans le modèle », le
  // premier modèle SERVI — pas un gabarit hérité, qui n'a pas d'adresse.
  const model = models.find(m => !m.isTemplate) ?? models[0] ?? null

  return (
    <div className={shellClass}>
      <BuilderTopHeader />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div>
          <Link
            to="/builder/types"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors mb-2"
          >
            <ChevronLeft size={12} aria-hidden="true" />
            Types de contenu
          </Link>
          <h1 className="text-2xl font-semibold text-gray-800">{type.name}</h1>
          <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-mono text-gray-400">{type.type}</span>
            <span className="inline-flex items-center gap-1">
              <Globe size={12} aria-hidden="true" />
              {type.siteName || 'Commun à tous les sites'}
            </span>
            {model && (
              <span>
                Modèle :{' '}
                <Link to={`/builder/${model.slug}`} className="text-teal-700 hover:text-teal-900">
                  {model.title || model.slug}
                </Link>
                {model.path && <span className="font-mono text-gray-400"> · {model.path}</span>}
              </span>
            )}
          </p>
        </div>

        <nav aria-label="Sections du type de contenu" className="flex items-center gap-6 border-b border-gray-200">
          {TABS.map((t) => {
            const TabIcon = t.icon
            return (
              <NavLink
                key={t.id}
                to={`${base}${t.suffix}`}
                end
                className={({ isActive }) => `flex items-center gap-1.5 pb-2 pt-1 -mb-px border-b-2 text-sm transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                  isActive
                    ? 'border-teal-700 text-teal-800 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <TabIcon size={14} aria-hidden="true" />
                {t.label}
              </NavLink>
            )
          })}
        </nav>

        {tab === 'contents' && (
          modelsLoading && models.length === 0
            ? <Spinner />
            : <TypeContentsTable plugins={plugins} type={type} modelSlug={model?.slug ?? ''} />
        )}
        {tab === 'model' && (
          <BuilderTypeModelTab type={type} models={models} loading={modelsLoading} onChanged={reloadModels} />
        )}
        {tab === 'schema' && (
          <SchemaTab type={type} onSaved={reloadType} />
        )}
      </main>
    </div>
  )
}

/**
 * Le tableau des contenus : un rjBuild (cf. CONTENTS_RJBUILD), monté avec les
 * valeurs du type. `dataOverride` REMPLACE le `data:` du YAML (il ne s'y fusionne
 * pas) : on part donc de celui qu'il déclare, et on n'y pose que nos clés.
 * Mémoïsé : un nouveau `dataOverride` ré-amorcerait le store, et effacerait une
 * recherche en cours à chaque re-rendu de l'écran.
 */
const TypeContentsTable = ({ plugins, type, modelSlug }) => {
  const [yamlText, setYamlText] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadContentsRjBuild()
      .then((text) => { if (!cancelled) setYamlText(text) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [])

  const dataOverride = useMemo(() => {
    if (!yamlText) return null
    let declared = {}
    try {
      declared = jsyaml.load(yamlText)?.data ?? {}
    } catch {
      declared = {}
    }
    return {
      ...declared,
      type: type.type,
      typeName: type.name,
      siteId: type.siteId ? String(type.siteId) : '',
      modelSlug: modelSlug || '',
      tableLabel: `Contenus du type ${type.name}`,
    }
  }, [yamlText, type.type, type.name, type.siteId, modelSlug])

  if (failed) {
    return <p className="text-sm text-red-700" role="alert">Impossible de charger la liste des contenus.</p>
  }
  if (!yamlText || !dataOverride) return <Spinner />

  return (
    <ReactiveJsonRoot
      key={`${type.type}:${modelSlug}`}
      maybeRawAppRjBuild={yamlText}
      dataOverride={dataOverride}
      rjBuildFetchMethod="GET"
      plugins={plugins}
    />
  )
}

/**
 * Onglet « Schéma » : le YAML brut de la définition (pas d'éditeur structuré
 * pour l'instant — l'auteur expérimenté édite directement ; la modale de
 * création pose un squelette), le rattachement d'un type hérité sans site, et
 * la suppression — refusée tant que des contenus existent.
 */
const SchemaTab = ({ type, onSaved }) => {
  const navigate = useNavigate()
  const { saveType, deleteType } = useBuilderObjectTypes()
  const { objects } = useBuilderObjects(type.type)
  const editorLabelId = useId()
  const editorHelpId = useId()

  const [definition, setDefinition] = useState(type.definition ?? '')
  const [originalDefinition, setOriginalDefinition] = useState(type.definition ?? '')
  const [savingDef, setSavingDef] = useState(false)
  const [defError, setDefError] = useState(null)
  const [yamlWarning, setYamlWarning] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (type?.definition != null) {
      setDefinition(type.definition)
      setOriginalDefinition(type.definition)
    }
  }, [type?.definition])

  // Live YAML lint — warns the author *as they type* if the structure is
  // broken or missing the bare-minimum keys. Soft warning : doesn't block
  // saving (some YAML are valid but unparseable mid-edit).
  useEffect(() => {
    if (!definition.trim()) {
      setYamlWarning('Définition vide.')
      return
    }
    try {
      const parsed = jsyaml.load(definition)
      if (!parsed || typeof parsed !== 'object') {
        setYamlWarning('La définition doit être un objet YAML.')
        return
      }
      if (!parsed.fields || !Array.isArray(parsed.fields) || parsed.fields.length === 0) {
        setYamlWarning('Aucun champ déclaré (`fields:` est requis).')
        return
      }
      setYamlWarning(null)
    } catch (e) {
      setYamlWarning(`YAML invalide : ${e.message}`)
    }
  }, [definition])

  const isDirty = definition !== originalDefinition

  const handleSaveDefinition = async () => {
    setSavingDef(true)
    setDefError(null)
    try {
      // Pas de `siteId` : clé absente ⇒ le rattachement ne bouge pas.
      await saveType({
        nid: type.nid,
        type: type.type,
        name: type.name,
        definition,
      })
      setOriginalDefinition(definition)
      onSaved?.()
    } catch (e) {
      setDefError(e.message || 'Erreur de sauvegarde.')
    } finally {
      setSavingDef(false)
    }
  }

  const handleDeleteType = async () => {
    if (!window.confirm(`Supprimer le type de contenu « ${type.name} » ? Cette action est irréversible.`)) {
      return
    }
    setDeleting(true)
    try {
      await deleteType({ nid: type.nid, type: type.type })
      navigate('/builder/types')
    } catch (e) {
      alert(e.message || 'Suppression impossible.')
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      {!type.siteId && <AttachSite type={type} onAttached={onSaved} />}

      <section className="bg-white border border-gray-200 rounded-xl">
        <header className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id={editorLabelId} className="text-sm font-semibold text-gray-800">Schéma (YAML)</h2>
            <p id={editorHelpId} className="text-xs text-gray-400 mt-0.5">
              Déclaration des champs du contenu et de leurs types.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {yamlWarning && (
              <span className="flex items-center gap-1 text-xs text-amber-700">
                <AlertCircle size={12} aria-hidden="true" />
                {yamlWarning}
              </span>
            )}
            <button
              type="button"
              onClick={handleSaveDefinition}
              disabled={!isDirty || savingDef}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={13} aria-hidden="true" />
              {savingDef ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </header>
        <textarea
          value={definition}
          onChange={(e) => setDefinition(e.target.value)}
          aria-labelledby={editorLabelId}
          aria-describedby={editorHelpId}
          spellCheck={false}
          rows={18}
          className="w-full px-5 py-4 text-xs font-mono text-gray-800 bg-gray-50/50 focus:outline-none focus:bg-white resize-y rounded-b-xl"
        />
        {defError && (
          <p className="px-5 py-3 text-xs text-red-600 border-t border-red-100 bg-red-50" role="alert">
            {defError}
          </p>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <h2 className="text-sm font-semibold text-gray-800">Supprimer ce type de contenu</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {objects.length > 0
              ? `Impossible tant qu'il reste des contenus (${objects.length}) : mettez-les d'abord à la corbeille.`
              : 'Irréversible. Son modèle redevient une page ordinaire du site.'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDeleteType}
          disabled={deleting || objects.length > 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 size={14} aria-hidden="true" />
          Supprimer le type
        </button>
      </section>
    </div>
  )
}

/**
 * Un type HÉRITÉ, d'avant la règle « un type de contenu appartient à un site » :
 * le rattacher. C'est la condition pour lui poser un modèle (qui porte
 * l'habillage d'un site) et y créer des contenus sans administrer tout le
 * builder. Écrit par POST /api/builder/def/site (relation de groupe). Les
 * contenus déjà créés gardent leur rattachement actuel.
 *
 * React et non rjBuild : un `<select>` et un POST, qu'un rjBuild (SelectField +
 * submitData) exprimerait aussi — mais ils vivent dans l'onglet « Schéma »,
 * écran React existant, où monter un rjBuild pour deux contrôles coûterait
 * plus qu'il ne rapporte. Un encart TRANSITOIRE : il disparaît dès le type
 * rattaché, et n'apparaît jamais pour un type créé depuis la refonte.
 */
const AttachSite = ({ type, onAttached }) => {
  const { sites, loading, error } = useBuilderSites()
  const headingId = useId()
  const selectId = useId()
  const [siteId, setSiteId] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)

  const attach = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await fetch('/api/builder/def/site', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ nid: type.nid, siteId: Number(siteId) }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) throw new Error(data.message || `Erreur ${res.status}`)
      onAttached?.()
    } catch (e) {
      setMessage(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-labelledby={headingId} className="alert alert-warning flex-col">
      <div>
        <h2 id={headingId} className="text-sm font-semibold">Ce type n&apos;est rattaché à aucun site</h2>
        <p className="text-xs mt-1">
          Un type de contenu appartient à un site : ses contenus y naissent et son modèle
          y est servi. Ce type date d&apos;avant cette règle — rattachez-le pour lui poser un
          modèle. Les contenus déjà créés gardent leur rattachement actuel.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={selectId} className="sr-only">Site</label>
        <select
          id={selectId}
          value={siteId}
          onChange={e => setSiteId(e.target.value)}
          disabled={loading || error}
          className="input"
        >
          <option value="">{loading ? 'Chargement des sites…' : 'Choisir un site…'}</option>
          {sites.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <button type="button" onClick={attach} disabled={!siteId || busy} className="btn btn-primary btn-sm">
          Rattacher
        </button>
      </div>
      {error && <p className="text-xs text-red-700" role="alert">Impossible de charger la liste des sites.</p>}
      {message && <p className="text-xs text-red-700" role="alert">{message}</p>}
    </section>
  )
}
